/**
 * Acquisition context for the `session_start` funnel event.
 *
 * Until now `session_start` carried NO metadata at all, so "how many visitors
 * came from Meta, from the Telegram bot, from search?" was unanswerable — the
 * events table has no referrer, no landing path and no source column, and UTMs
 * were captured in the browser but only ever reached the Meta CAPI legs.
 *
 * Deliberately narrow about what it keeps:
 *  - the referrer's HOST, never the full URL. "google.com" answers the question;
 *    the visitor's previous full URL is somebody else's page and can carry their
 *    query terms.
 *  - the landing PATHNAME, never the query string, which can hold arbitrary
 *    user input.
 *  - a small allowlist of source markers (`src`, `utm_source`, `utm_medium`,
 *    `utm_campaign`, `utm_term`, `utm_content`), each length-capped. On our Meta
 *    links the last two name the ad set and the ad, which is what lets the
 *    first-touch backfill say WHICH ad brought a user, not just which campaign.
 */

/** Values kept small so one row per session stays cheap in `events.meta`. */
const MAX_LEN = 64;

const clip = (v: string | null | undefined): string | undefined =>
  v ? v.slice(0, MAX_LEN) : undefined;

/**
 * Referrer host, or `'direct'` when there is none. Same-origin referrers become
 * `'internal'` — a visitor arriving from our own page is not an acquisition
 * source, and counting them as one would inflate whichever page they came from.
 */
export function referrerHost(): string {
  if (typeof document === 'undefined') return 'direct';
  const raw = document.referrer;
  if (!raw) return 'direct';
  try {
    const host = new URL(raw).hostname.replace(/^www\./, '');
    if (typeof window !== 'undefined' && host === window.location.hostname.replace(/^www\./, '')) {
      return 'internal';
    }
    return host.slice(0, MAX_LEN);
  } catch {
    return 'unknown';
  }
}

/**
 * The acquisition context to attach to `session_start`.
 *
 * A `type`, not an `interface`, on purpose: only type aliases get an implicit
 * index signature, so this stays assignable to the `Record<string, unknown>`
 * that `trackOnce` takes as event meta.
 */
export type SessionContext = {
  /** Referrer host, `direct`, `internal`, or `unknown`. */
  ref: string;
  /** Landing pathname, query stripped. */
  landing: string;
  /** `?src=` marker — `tg` on links we send into Telegram and email. */
  src?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  /** Meta links: `{{adset.name}}`. */
  utm_term?: string;
  /** Meta links: `{{ad.name}}`. */
  utm_content?: string;
};

export function sessionContext(): SessionContext {
  if (typeof window === 'undefined') return { ref: 'direct', landing: '/' };
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams();
  }
  return {
    ref: referrerHost(),
    landing: window.location.pathname.slice(0, MAX_LEN) || '/',
    src: clip(params.get('src')),
    utm_source: clip(params.get('utm_source')),
    utm_medium: clip(params.get('utm_medium')),
    utm_campaign: clip(params.get('utm_campaign')),
    utm_term: clip(params.get('utm_term')),
    utm_content: clip(params.get('utm_content')),
  };
}
